import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import {
  BillingInformationModel,
  StripeCreditCardPaymentSourceModel,
  StripePaymentSourceModel,
} from '../../../api/billing-types';
import { useState } from 'react';
import DeletePaymentMethodDialog from './delete-payment-method-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { BillingKeys } from '../../../api/billing-api';
import AddPaymentMethodDialog from './add-payment-method-dialog';
import UpdateAutoPayDialog from './update-autopay-dialog';
import Visa from '../../../assets/cards/visa.png';
import Amex from '../../../assets/cards/amex.png';
import Mastercard from '../../../assets/cards/mastercard.png';
import Discover from '../../../assets/cards/discover.png';
import PaymentMethodsEditCreditCardForm from './payment-methods-edit-credit-card-form';
import PaymentMethodEditAchForm from './payment-method-edit-ach-form';
import ValidateHubspotContactDialog from '../validate-hubspot-contact/validate-hubspot-contact-dialog';
import { HubSpotCompany } from '../../../api/billing-types';
import { validatePrimaryBillingContact } from '../../../shared/hubspot-address-utils';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AccountBalance from '@mui/icons-material/AccountBalance';
import AddCard from '@mui/icons-material/AddCard';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Refresh from '@mui/icons-material/Refresh';
import Payments from '@mui/icons-material/Payments';
import Receipt from '@mui/icons-material/Receipt';
import AssuredWorkload from '@mui/icons-material/AssuredWorkload';
import SharedTabHeader from '../../../shared/shared-tab-header';
import SharedButton from '../../../shared/buttons/shared-button';

interface PaymentMethodsListProps {
  billingInfo: BillingInformationModel;
  changeLogRoles: string[];
  billingRoles: string[];
  hubSpotCompany: HubSpotCompany;
  descriptionText: string;
}

export default function PaymentMethodsList({
  billingInfo,
  changeLogRoles,
  billingRoles,
  hubSpotCompany,
  descriptionText,
}: PaymentMethodsListProps) {
  const [openDeletePaymentMethodDialog, setOpenDeletePaymentMethodDialog] =
    useState(false);
  const [openAddPaymentMethodDialog, setOpenAddPaymentMethodDialog] =
    useState(false);
  const [openUpdateAutoPayDialog, setOpenUpdateAutoPayDialog] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<StripePaymentSourceModel | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [
    openValidateHubspotContactDialog,
    setOpenValidateHubspotContactDialog,
  ] = useState(false);

  const queryClient = useQueryClient();

  const images = {
    Visa: Visa,
    'American Express': Amex,
    MasterCard: Mastercard,
    Discover: Discover,
  };

  const canDeletePaymentMethod = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canRemovePaymentMethods.value
  );

  const canAddPaymentMethod = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canAddPaymentMethods.value
  );

  const canUpdateAutoPay = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canChangeAutoPaySource.value
  );

  const hasNoPaymentMethods = billingInfo.stripeSourceModels.length === 0;

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  function isCreditCard(
    paymentMethod: StripePaymentSourceModel
  ): paymentMethod is StripeCreditCardPaymentSourceModel {
    return paymentMethod.stripePaymentSourceType === 'CreditCard';
  }

  return (
    <Box sx={{ position: 'relative', minHeight: 500 }}>
      <SharedTabHeader title="Payment Methods">
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="center"
        >
          <Tooltip
            arrow
            title={
              canUpdateAutoPay
                ? 'Update Autopay'
                : hasNoPaymentMethods
                ? 'You need to add a payment method before you can update autopay.'
                : `You need the permission "${BillingRoles.canChangeAutoPaySource.name}"  to update autopay.`
            }
          >
            <span>
              <IconButton
                onClick={() => {
                  setOpenUpdateAutoPayDialog(true);
                }}
                disabled={!canUpdateAutoPay || hasNoPaymentMethods}
              >
                <Payments />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip arrow title="Refresh">
            <IconButton
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: [BillingKeys.CustomerBillingInformation],
                });
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              canAddPaymentMethod
                ? ''
                : `You need the permission "${BillingRoles.canAddPaymentMethods.name}"  to add a payment method.`
            }
          >
            <span>
              <SharedButton
                variant="primary"
                onClick={() => {
                  try {
                    validatePrimaryBillingContact(billingInfo);
                    setOpenAddPaymentMethodDialog(true);
                  } catch {
                    setOpenValidateHubspotContactDialog(true);
                  }
                }}
                endIcon={<AddCard />}
                disabled={!canAddPaymentMethod}
              >
                Add Payment
              </SharedButton>
            </span>
          </Tooltip>
        </Stack>
      </SharedTabHeader>
      <Typography variant="body1" sx={{ my: 2, px: 1 }}>
        {descriptionText}
      </Typography>

      <Box sx={{ maxWidth: 1000, mx: 'auto', my: 2 }}>
        {billingInfo.stripeSourceModels.map((paymentMethod) => {
          const isCC = isCreditCard(paymentMethod);

          return (
            <Accordion
              key={paymentMethod.id}
              expanded={expanded === paymentMethod.id.toString()}
              onChange={handleChange(paymentMethod.id.toString())}
              disableGutters
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Avatar
                      src={images[paymentMethod.brand as keyof typeof images]}
                      variant="rounded"
                    >
                      <AccountBalance />
                    </Avatar>
                    <Stack>
                      {isCC ? (
                        <>
                          <Typography variant="body1">
                            {paymentMethod.metadata?.nickName
                              ? paymentMethod.metadata?.nickName
                              : paymentMethod.brand +
                                '****' +
                                paymentMethod.last4}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Expires {paymentMethod.exp_month}/
                            {paymentMethod.exp_year.toString().slice(-2)}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body1">
                            {paymentMethod.metadata?.nickName
                              ? paymentMethod.metadata?.nickName
                              : paymentMethod.bank_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {paymentMethod.bank_name} - x{paymentMethod.last4}
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    gap={1}
                    sx={{ mr: 2 }}
                  >
                    {paymentMethod.showWarning && (
                      <Tooltip
                        title='You need to verify your account before being able to use it as a payment method. Verify your account by opening the accordion and clicking on the "Verify" button.'
                        arrow
                      >
                        <Chip
                          color="warning"
                          label="verification required"
                          size="small"
                          icon={<AssuredWorkload />}
                        />
                      </Tooltip>
                    )}

                    {paymentMethod.metadata?.isAutoPaySource && (
                      <Chip
                        label="autopay"
                        size="small"
                        color="primary"
                        icon={<Payments />}
                      />
                    )}

                    <Tooltip
                      title={
                        canDeletePaymentMethod
                          ? 'Delete'
                          : `You need the permission "${BillingRoles.canRemovePaymentMethods.name}"  to delete this payment method.`
                      }
                    >
                      <span>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPaymentMethod(paymentMethod);
                            setOpenDeletePaymentMethodDialog(true);
                          }}
                          disabled={!canDeletePaymentMethod}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </AccordionSummary>
              {isCC ? (
                <PaymentMethodsEditCreditCardForm
                  billingRoles={billingRoles}
                  paymentMethod={paymentMethod}
                  billingInfoModel={billingInfo}
                  changeLogRoles={changeLogRoles}
                />
              ) : (
                <PaymentMethodEditAchForm
                  billingRoles={billingRoles}
                  paymentMethod={paymentMethod}
                  billingInfoModel={billingInfo}
                  changeLogRoles={changeLogRoles}
                />
              )}
            </Accordion>
          );
        })}
        <Accordion
          key={'invoice'}
          expanded={expanded === 'invoice'}
          onChange={handleChange('invoice')}
          disableGutters
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <Avatar variant="rounded">
                  <Receipt />
                </Avatar>
                <Stack>
                  <Typography variant="body1">Invoice</Typography>
                  <Typography variant="caption" color="text.secondary">
                    You will received an invoice 30 days prior to your renewal
                    date
                  </Typography>
                </Stack>
              </Stack>

              <Stack
                flexDirection="row"
                alignItems="center"
                gap={1}
                sx={{ mr: 2 }}
              >
                {billingInfo.stripeSourceModels.some(
                  (source) => source.metadata?.isAutoPaySource
                ) ? (
                  <Chip label="backup" size="small" icon={<Payments />} />
                ) : (
                  <Chip label="default" size="small" icon={<Payments />} />
                )}
                <Box width={40} />
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>Invoice</Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
      {openDeletePaymentMethodDialog && selectedPaymentMethod ? (
        <DeletePaymentMethodDialog
          open={openDeletePaymentMethodDialog}
          handleClose={() => setOpenDeletePaymentMethodDialog(false)}
          selectedMethod={selectedPaymentMethod}
          billingInfoModel={billingInfo}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openAddPaymentMethodDialog ? (
        <AddPaymentMethodDialog
          open={openAddPaymentMethodDialog}
          handleClose={() => setOpenAddPaymentMethodDialog(false)}
          changeLogRoles={changeLogRoles}
          billingInfoModel={billingInfo}
          hubSpotCompany={hubSpotCompany}
        />
      ) : null}
      {openUpdateAutoPayDialog ? (
        <UpdateAutoPayDialog
          open={openUpdateAutoPayDialog}
          handleClose={() => setOpenUpdateAutoPayDialog(false)}
          changeLogRoles={changeLogRoles}
          billingInfoModel={billingInfo}
          hubSpotCompany={hubSpotCompany}
        />
      ) : null}
      {openValidateHubspotContactDialog ? (
        <ValidateHubspotContactDialog
          open={openValidateHubspotContactDialog}
          handleClose={() => setOpenValidateHubspotContactDialog(false)}
          billingInformationModel={billingInfo}
          setOpenAddPaymentMethodDialog={setOpenAddPaymentMethodDialog}
          hubSpotCompany={hubSpotCompany}
        />
      ) : null}
    </Box>
  );
}
